/**
 * A tiny client-side cache for read-only GET requests, with in-flight
 * de-duplication.
 *
 * The public site fetches its data from Client Components on mount. Now that
 * the Navbar and Footer persist across navigation, the *pages* still remount,
 * so every visit re-ran the same requests: `/api/categories` is needed by the
 * Navbar's category menu, the home page, and the categories page, and
 * `/api/courses` by the home, courses, categories and category-detail pages.
 * Going home -> courses -> home refetched all of it three times.
 *
 * This wraps a fetcher so that:
 *  - identical concurrent calls share one request (de-duplication), which alone
 *    removes the double-fetch caused by the Navbar and the page body asking for
 *    categories at the same moment;
 *  - a resolved value is reused for `ttlMs`, so back-and-forth navigation is
 *    instant instead of re-hitting the network.
 *
 * Deliberately not a general data layer. There is no revalidation, no cache
 * invalidation API and no persistence - it is scoped to public, read-only,
 * slow-changing catalogue data. Anything user-specific or mutable must not use
 * it. If the app ever needs real cache semantics, replace this with TanStack
 * Query rather than growing it.
 */

type CacheEntry<T> = {
  value: T;
  expiresAt: number;
};

const DEFAULT_TTL_MS = 5 * 60 * 1000;

const resolved = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Runs `fetcher`, reusing a cached or in-flight result for the same `key`.
 *
 * A rejected request is never cached and is removed from the in-flight map, so
 * the next caller retries rather than inheriting the failure.
 */
export async function cachedRequest<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS,
): Promise<T> {
  const now = Date.now();
  const hit = resolved.get(key);

  if (hit && hit.expiresAt > now) {
    return hit.value as T;
  }

  const pending = inFlight.get(key);
  if (pending) {
    return pending as Promise<T>;
  }

  const request = fetcher()
    .then((value) => {
      resolved.set(key, { value, expiresAt: Date.now() + ttlMs });
      return value;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);

  return request as Promise<T>;
}

/**
 * Builds a stable cache key from a name and a set of parameters. Entries are
 * sorted so that argument order can never produce two keys for one request.
 */
export function buildCacheKey(
  name: string,
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${String(v)}`);

  return parts.length > 0 ? `${name}?${parts.join("&")}` : name;
}

/** Drops every cached entry. Exposed for tests. */
export function clearRequestCache(): void {
  resolved.clear();
  inFlight.clear();
}
